import { ENV } from '../../config/runtimeEnv';
import type {
	Stock,
	StockFilterParams,
	StockCreateParams,
	StockCreateResponse,
	StockUpdateParams,
	ProductStockSummary,
	VariantStockSummary,
	ProductLabelsResponse,
} from '../../types/inventory/StockTypes';
import { http } from '../../lib/http/client';

// Note: productVariantApi.ts is the source of truth for ProductVariant type.
// stockApi.ts uses a forward-reference-safe import to avoid circular deps.
const API_BASE_URL = ENV.VITE_COMMERCE_SERVER;

// ─── API functions ────────────────────────────────────────────────────────────

export const fetchStockEntries = async (
	filters?: StockFilterParams
): Promise<{ data: Stock[]; total: number }> => {
	const response = await http.get(`${API_BASE_URL}/stock`, {
		params: filters,
	});
	return {
		data: response.data.data,
		total: response.data.pagination?.total_items ?? response.data.total ?? 0,
	};
};

export const fetchStockEntry = async (stock_id: number): Promise<Stock> => {
	const response = await http.get(`${API_BASE_URL}/stock/${stock_id}`);
	return response.data;
};

export const createStockEntry = async (
	stockData: StockCreateParams
): Promise<StockCreateResponse> => {
	const response = await http.post(`${API_BASE_URL}/stock`, stockData);
	return response.data;
};

export const updateStockEntry = async (
	stock_id: number,
	stockData: StockUpdateParams
): Promise<Stock> => {
	const response = await http.put(`${API_BASE_URL}/stock/${stock_id}`, stockData);
	return response.data;
};

export const deleteStockEntry = async (stock_id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/stock/${stock_id}`);
};

export const getProductStockSummary = async (
	product_id: number
): Promise<ProductStockSummary> => {
	const response = await http.get(
		`${API_BASE_URL}/stock/product/${product_id}/summary`
	);
	return response.data;
};

export const getVariantStockSummary = async (
	variant_id: number
): Promise<VariantStockSummary> => {
	const response = await http.get(
		`${API_BASE_URL}/stock/variant/${variant_id}/summary`
	);
	return response.data;
};

/** Fetches all labels in the product-wide shared pool with optional status filter. */
export const fetchProductLabels = async (
	productId: number,
	params?: { status?: 'available' | 'assigned'; page?: number; per_page?: number }
): Promise<ProductLabelsResponse> => {
	const response = await http.get(
		`${API_BASE_URL}/stock/product/${productId}/labels`,
		{
			params,
		}
	);
	return response.data;
};

export interface AssignStockLabelParams {
	label_id: number;
	sales_order_id: number;
	sales_order_item_id: number;
	/** Required only when the order item already holds more than one label. */
	replace_stock_label_id?: number;
}

export interface AssignStockLabelResult {
	label_id: string;
	stock_label_id: number;
	order_label_id: number;
	stock_id: number;
	replaced_stock_label_id: number | null;
}

/**
 * Manual override/correction on top of FIFO auto-assignment: assigns (or
 * reassigns) one specific label to one specific sales order item.
 */
export const assignStockLabel = async (
	params: AssignStockLabelParams
): Promise<AssignStockLabelResult> => {
	const response = await http.put(
		`${API_BASE_URL}/stock/labels/assign`,
		params
	);
	return response.data.data;
};